<script setup lang="ts">
import { useUserModeChange } from "@/store/useUserModeChange";
import { getPosPrinter } from "@/utils/common";
import { formatPrice } from "@/utils/Formatting";
import { pb } from "@/utils/PocketBaseAdapter";
import { ref } from "vue";
import { useRoute, useRouter } from "vue-router";

const router = useRouter()
const route = useRoute()
interface Props {
  isAddCardDialogVisible: boolean;
  addCardDialogMessage: any;
  totalAmount?: number;
  surchargeAmount?: number;
  cardIsProcessing? :boolean
}

interface Emits {
  (e: "closeDialog", value: boolean): void;
  (e: "emailReceipt"): void;
  (e: "printReceipt", printer: string): void;
}

// Props
const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Store
const modeStore = useUserModeChange();
const { isUserMode } = storeToRefs(modeStore);

// Data
const printer = ref<string>('');
const printerList = ref<Array<{ id: string; name: string }>>([]);
const isAdmin = pb.authStore?.model?.roles.length > 0 && !isUserMode.value;

const refreshPage = () => {
  // Navigation is handled by the parent after the confirm dialog closes
}

const canOrderFood = ref(false)
const bookingId = ref('')
const checkIfFoodCanBeOrdered = async () => {
  if (route?.query?.dpid) {
    try {
      let getDocketPayment = await pb.collection('docketPayments').getOne(route?.query?.dpid)
      let bookingCheck = await pb.collection("docketLines").getFullList({filter: 'docket="' + getDocketPayment?.docket + '" && transactionType="Booking Payment"'})
      if (bookingCheck) {
        bookingId.value = bookingCheck.filter(bc => bc?.booking)[0]?.booking
      }
    } catch(e){}
    let checkFood = await pb.collection("products").getFullList({
      filter: 'canBuyInWebshop=true && notDirectlySold=false',
      expand: 'category',
      requestKey: null,
    })
    if (checkFood) {
      checkFood.forEach(item => {
        if (item?.expand?.category?.isFood) {
          canOrderFood.value = bookingId.value != '' && bookingId.value != undefined ? true : false
        }
      })
    }
  }
}

onMounted(async()=>{
  window.setTimeout(() => {
    checkIfFoodCanBeOrdered()
  },500)

  try {
    printer.value = await getPosPrinter();
    const printers = await pb.collection("printers").getFullList({filter:'printerType!="Label"', requestKey: null});
    printerList.value = printers.map((item) => ({
      id: item.id,
      name: item.name
    }));
  } catch (error) {
    console.error('Failed to load printers:', error);
  }
})

const goToFood = async () => {
  emit("closeDialog", false);
  router.push('/webshop?b=' + bookingId.value)
}
</script>

<template>
  <div>
    <VDialog
      :model-value="props.isAddCardDialogVisible"
      persistent
      max-width="600"
      max-height="800"
      no-click-animation
      @update:model-value="emit('closeDialog', false)"
    >
      <VCard>
        <div
          class="d-flex align-center justify-space-between bg-dialogHeaderColor"
        >
          <VCardTitle> Card Payment </VCardTitle>

          <VBtn
            icon="mdi-close"
            size="large"
            variant="text"
            class="rounded-0"
            color=""
            @click="refreshPage();emit('closeDialog', false)"
          />
        </div>

        <VCardText class="text-center">
          <VProgressCircular
            v-if="props?.cardIsProcessing || (
              props.addCardDialogMessage?.spinner &&
              props.addCardDialogMessage?.isVisible)
            "
            :color="props.addCardDialogMessage?.color"
            indeterminate
            :size="50"
            variant="outlined"
          />
          <div class="text-center my-4">
            <span class="text-h5">
              Payment of {{ formatPrice(props.totalAmount) }} with Card
            </span>
            <span v-if="props.surchargeAmount" class="surcharge-info">
              ( including surcharge of {{ formatPrice(props.surchargeAmount) }}
              )
            </span>
          </div>
          <p v-if="!props?.cardIsProcessing"
            class="text-h4 my-5"
            :class="`text-${props.addCardDialogMessage?.color}`"
          >
            {{ props.addCardDialogMessage?.message }}
          </p>

          <v-select
            v-if="printerList.length > 1 && isAdmin && props.addCardDialogMessage?.status === 'Approved'"
            v-model="printer"
            :items="printerList"
            item-title="name"
            item-value="id"
            label="Printer"
            class="mt-4"
          />
        </VCardText>

        <VCardActions v-if="!props?.cardIsProcessing">
          <div v-if="props.addCardDialogMessage?.status === 'Approved'">
            <VBtn
              color="primary"
              variant="flat"
              class="rounded-sm"
              @click="emit('emailReceipt')"
            >
              Email Receipt
            </VBtn>
            <VBtn
              v-if="isAdmin"
              color="primary"
              variant="flat"
              class="rounded-sm ml-3"
              @click="emit('printReceipt', printer)"
            >
              Print Receipt
            </VBtn>
            <VBtn v-if="canOrderFood"
              color="primary"
              variant="flat"
              prepend-icon="mdi-food"
              @click.prevent="goToFood()"
              class="rounded-sm ml-3">
              Order Food
            </VBtn>
          </div>
          <VSpacer />
          <VBtn
            color="error"
            variant="flat"
            class="rounded-sm"
            @click="refreshPage(); emit('closeDialog', false)"
          >
            Close
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
